/**
 * Защо данните на родителя НЕ са показани — видимо, никога празно и никога „ето всичко“.
 *
 * 19.10.2026, № 1116: страницата „Декларации“ показваше само заглавието при 5 възлагания на
 * сървъра — нито „зарежда“, нито грешка, нито „няма“. Родителят не можеше да каже дали няма
 * декларации, дали профилът не е свързан, или дали приложението не стига до базата. Едно място
 * за таблото, „Декларации“ и „Деца“, за да не се разминат.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPanel } from '../ui';
import { isNoServerResponse } from '../../hooks/useFirestoreQuery';

/** Заявката е паднала — или сървърът не е достигнат (`NoServerResponseError`), или друга грешка. */
export function LoadProblem({ error, onRetry, failedTitle, offlineBody }: {
  error: Error;
  onRetry: () => void;
  /** Заглавието при друга грешка — какво не се зареди. */
  failedTitle: string;
  /** Текстът при „без сървър“ — какво не е показано и защо това НЕ значи „няма“. */
  offlineBody: ReactNode;
}) {
  const { t } = useTranslation();
  const offline = isNoServerResponse(error);
  return (
    <StatusPanel
      variant="danger"
      title={offline ? t('common.noServerTitle') : failedTitle}
      actions={
        <button type="button" className="btn btn-secondary text-sm" onClick={onRetry}>
          {t('common.retry')}
        </button>
      }
    >
      {offline ? offlineBody : error.message}
    </StatusPanel>
  );
}

/**
 * Обновяването падна, а на екрана са данните от по-ранно зареждане от сървъра. Бележка над тях —
 * НЕ вместо тях: отворена форма за подпис не се затваря заради фоново обновяване (ревю кр.2).
 */
export function RefreshProblem({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <StatusPanel
      variant="warning"
      title={t('common.refreshFailedTitle')}
      actions={
        <button type="button" className="btn btn-secondary text-sm" onClick={onRetry}>
          {t('common.retry')}
        </button>
      }
    >
      {isNoServerResponse(error) ? t('common.refreshFailedBody') : `${t('common.refreshFailedBody')} (${error.message})`}
    </StatusPanel>
  );
}

/** Декларациите на семейството не са заредени. */
export function AssignmentsLoadProblem({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <LoadProblem
      error={error}
      onRetry={onRetry}
      failedTitle={t('assignments.state.loadFailedTitle')}
      offlineBody={t('assignments.state.offlineBody')}
    />
  );
}

/** В сесията няма семейство — декларациите не са потърсени, не „няма“. Текстът е този на таблото. */
export function AssignmentsNoFamily() {
  const { t } = useTranslation();
  return (
    <StatusPanel variant="warning" title={t('dashboard.notLinked.title')}>
      <p>{t('dashboard.notLinked.body')}</p>
      <p className="mt-1">{t('assignments.state.reloginHint')}</p>
    </StatusPanel>
  );
}
